import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShieldCheck, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const BetaActivatePage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If user is already a beta_user, redirect
  if (user?.role === 'beta_user' || user?.role === 'admin') {
    navigate('/app/select-school', { replace: true });
    return null;
  }

  const handleActivate = async () => {
    if (!code.trim()) { setError('Please enter an invite code'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/auth/activate-beta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: code.trim() }),
      });
      if (r.ok) {
        toast.success('Beta access activated!');
        await checkAuth();
        navigate('/app/select-school', { replace: true });
      } else {
        const err = await r.json();
        setError(err.detail || 'Invalid invite code');
      }
    } catch (e) {
      setError('Failed to activate. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="beta-activate-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Beta Access Required</h1>
          <p className="text-sm text-slate-500 mt-2">
            Welcome, {user?.name}! Reliefify is in closed beta. Enter your invite code to unlock full access.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 space-y-4">
          <Input
            placeholder="Enter invite code"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
            className="border-slate-200 rounded-xl text-center text-lg font-mono tracking-widest h-14 uppercase"
            data-testid="beta-activate-input"
          />
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1" data-testid="beta-activate-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
          <Button
            onClick={handleActivate}
            disabled={loading || !code.trim()}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 text-base"
            data-testid="beta-activate-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            {loading ? 'Activating...' : 'Activate Beta Access'}
          </Button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Don't have an invite code? Contact the Reliefify team.
        </p>
      </div>
    </div>
  );
};
